import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, MessageSquare, Send, Ban, Plus, X } from "lucide-react";
import { plannerApi } from "./plannerApi";
import { ReminderScheduleFields } from "./ReminderScheduleFields";
import { DeliveryStatusChip } from "./DeliveryStatusChip";
import { PLANNER_CATEGORIES } from "@/constants/plannerCategories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const emptyForm = (defaultDate) => ({
  parentName: "", parentEmail: "", parentPhone: "", children: [{ firstName: "", birthdate: "" }],
  date: defaultDate || "", startTime: "10:00", endTime: "", category: "", notes: "",
  parentReminder: { enabled: false, channels: { email: true, sms: false }, schedule: [{ offsetMinutesBeforeStart: 1440 }] },
  staffAlert: { enabled: false, notifyDirector: true, recipientStaffIds: [], channels: { email: true, sms: false }, schedule: [{ offsetMinutesBeforeStart: 1440 }] },
});

// Reads either the new multi-staff list or an older single-recipient tour, so existing tours
// display and behave exactly as before.
const staffIdsOf = (staffAlert) =>
  staffAlert?.recipientStaffIds?.length ? staffAlert.recipientStaffIds
    : (staffAlert?.recipientStaffId ? [staffAlert.recipientStaffId] : []);

export const ScheduleTourModal = ({ open, onClose, rooms, tour, defaultDate, onSaved, onCancelled }) => {
  const [form, setForm] = useState(emptyForm(defaultDate));
  const [staffList, setStaffList] = useState([]);
  const [ownerName, setOwnerName] = useState("Organization Owner");
  const [ownerStaffId, setOwnerStaffId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [confirmInfo, setConfirmInfo] = useState(null); // { names: [], channels: [] } when open

  const isEdit = !!tour;

  useEffect(() => {
    if (open) {
      setForm(tour ? {
        parentName: tour.parentName || "", parentEmail: tour.parentEmail || "", parentPhone: tour.parentPhone || "",
        children: (tour.children && tour.children.length > 0)
          ? tour.children.map((c) => ({ firstName: c.firstName || "", birthdate: c.birthdate || "", suggestedRoomId: c.suggestedRoomId }))
          : [{ firstName: tour.childFirstName || "", birthdate: tour.childBirthdate || "" }],
        date: tour.date || "", startTime: tour.startTime || "10:00", endTime: tour.endTime || "",
        selectedRoomId: tour.selectedRoomId || "", category: tour.category || "", notes: tour.notes || "",
        parentReminder: tour.parentReminder || emptyForm().parentReminder,
        staffAlert: tour.staffAlert
          ? { ...emptyForm().staffAlert, ...tour.staffAlert, recipientStaffIds: staffIdsOf(tour.staffAlert) }
          : emptyForm().staffAlert,
      } : emptyForm(defaultDate));
      axios.get(`${API}/staff-records`, auth()).then((r) => setStaffList(r.data?.records || [])).catch(() => {});
      plannerApi.getSettings().then((r) => {
        setOwnerName(r.data?.ownerName || "Organization Owner");
        setOwnerStaffId(r.data?.ownerStaffId || null);
      }).catch(() => {});
    }
  }, [open, tour, defaultDate]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setPR = (patch) => setForm((f) => ({ ...f, parentReminder: { ...f.parentReminder, ...patch } }));
  const setSA = (patch) => setForm((f) => ({ ...f, staffAlert: { ...f.staffAlert, ...patch } }));
  const addChild = () => setForm((f) => ({ ...f, children: [...f.children, { firstName: "", birthdate: "" }] }));
  const removeChild = (idx) => setForm((f) => ({ ...f, children: f.children.filter((_, i) => i !== idx) }));
  const updateChild = (idx, patch) => setForm((f) => ({ ...f, children: f.children.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));
  const toggleStaffRecipient = (id) => setSA({
    recipientStaffIds: form.staffAlert.recipientStaffIds.includes(id)
      ? form.staffAlert.recipientStaffIds.filter((x) => x !== id)
      : [...form.staffAlert.recipientStaffIds, id],
  });
  // One-click preset: check the Organization Owner + every staff member at once, instead of
  // clicking each checkbox individually.
  const applyAllStaffOwnerPreset = () => setSA({ notifyDirector: true, recipientStaffIds: selectableStaffList.map((s) => s.id) });

  const save = async () => {
    if (!form.parentName.trim() || !form.date || !form.startTime) {
      toast.error("Parent name, date, and start time are required");
      return;
    }
    const children = form.children
      .filter((c) => c.firstName?.trim() || c.birthdate)
      .map((c) => ({ firstName: c.firstName || "", birthdate: c.birthdate || "" }));
    const payload = { ...form, children, selectedRoomId: form.selectedRoomId || null, category: form.category || null };
    setSaving(true);
    try {
      if (isEdit) {
        await plannerApi.updateTour(tour.id, payload);
        toast.success("Tour updated");
      } else {
        await plannerApi.createTour(payload);
        toast.success("Tour scheduled");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save tour");
    } finally {
      setSaving(false);
    }
  };

  const cancelTour = async () => {
    if (!window.confirm("Cancel this tour? It will move to the Archive.")) return;
    try {
      await plannerApi.cancelTour(tour.id);
      toast.success("Tour cancelled");
      onCancelled();
    } catch {
      toast.error("Failed to cancel tour");
    }
  };

  const channelLabel = { email: "Email", sms: "SMS" };

  // Staff checkbox list should never include the Organization Owner's own staff record —
  // otherwise checking both "Notify Organization Owner" and their own staff checkbox would
  // message them twice.
  const selectableStaffList = staffList.filter((s) => s.id !== ownerStaffId);

  // Legacy tours (saved before multi-staff support) stored delivery status under one fixed
  // "staffMember" key. Fall back to it ONLY when there's exactly one staff recipient, so an
  // old tour's existing "Sent" status still displays correctly without ever mixing up two
  // different people's status.
  const staffBlock = (occ, id, channel) =>
    occ.recipients?.[`staffMember:${id}`]?.[channel]
    ?? (form.staffAlert.recipientStaffIds.length === 1 ? occ.recipients?.staffMember?.[channel] : undefined);

  // Opens the confirmation dialog for the PARENT quick-send, using the parent's actual name and
  // whichever Email/SMS channels are selected for the Parent Reminder above.
  const openParentSendNowConfirm = () => {
    if (!form.parentName.trim()) {
      toast.error("Enter a parent name above before sending");
      return;
    }
    const channels = Object.entries(form.parentReminder.channels).filter(([, v]) => v).map(([k]) => channelLabel[k]);
    if (channels.length === 0) {
      toast.error("Select at least one channel (Email or SMS) above");
      return;
    }
    setConfirmInfo({ names: [form.parentName], channels, kind: "parent" });
  };

  // Opens the confirmation dialog, listing the exact people and channels this will message —
  // computed live from the shared recipient selection above, not a separate selection.
  const openSendNowConfirm = () => {
    const names = [];
    if (form.staffAlert.notifyDirector) names.push(`Organization Owner (${ownerName})`);
    form.staffAlert.recipientStaffIds.forEach((id) => {
      const s = staffList.find((x) => x.id === id);
      if (s) names.push(s.name);
    });
    if (names.length === 0) {
      toast.error("Select at least one recipient (Organization Owner or a staff member) above");
      return;
    }
    const channels = Object.entries(form.staffAlert.channels).filter(([, v]) => v).map(([k]) => channelLabel[k]);
    if (channels.length === 0) {
      toast.error("Select at least one channel (Email or SMS) above");
      return;
    }
    setConfirmInfo({ names, channels, kind: "staff" });
  };

  const confirmSendNow = async () => {
    const kind = confirmInfo?.kind;
    setConfirmInfo(null);
    setSendingNow(true);
    try {
      let res;
      if (kind === "parent") {
        res = await plannerApi.sendReminderNow(tour.id, { recipients: ["parent"], channels: form.parentReminder.channels });
      } else {
        const recipients = [];
        if (form.staffAlert.notifyDirector) recipients.push("director");
        if (form.staffAlert.recipientStaffIds.length) recipients.push("staff");
        res = await plannerApi.sendReminderNow(tour.id, {
          recipients, channels: form.staffAlert.channels, recipientStaffIds: form.staffAlert.recipientStaffIds,
        });
      }
      const results = res.data.results || [];
      const okCount = results.filter((r) => r.sent).length;
      if (results.length > 0 && okCount === results.length) {
        toast.success(`Sent to ${[...new Set(results.map((r) => r.recipient))].join(", ")}`);
      } else if (okCount > 0) {
        const failed = results.filter((r) => !r.sent).map((r) => `${r.recipient} — ${channelLabel[r.channel]}: ${r.reason || "failed"}`);
        toast.warning(`Partially sent. Failed: ${failed.join("; ")}`);
      } else {
        const failed = results.map((r) => `${r.recipient} — ${channelLabel[r.channel]}: ${r.reason || "failed"}`);
        toast.error(`Send failed: ${failed.join("; ")}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSendingNow(false);
    }
  };

  const prOccurrences = form.parentReminder?.occurrences || [];
  const saOccurrences = form.staffAlert?.occurrences || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto bg-white" data-testid="schedule-tour-modal">
        <DialogHeader>
          <DialogTitle className="text-slate-800">{isEdit ? "Edit Tour" : "Schedule a Tour"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Parent Name *</Label>
              <Input value={form.parentName} onChange={(e) => set({ parentName: e.target.value })} data-testid="tour-parent-name-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Parent Email</Label>
              <Input type="email" value={form.parentEmail} onChange={(e) => set({ parentEmail: e.target.value })} data-testid="tour-parent-email-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Parent Phone</Label>
              <Input value={form.parentPhone} onChange={(e) => set({ parentPhone: e.target.value })} data-testid="tour-parent-phone-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Child First Name</Label>
              <Input value={form.children[0]?.firstName || ""} onChange={(e) => updateChild(0, { firstName: e.target.value })} data-testid="tour-child-name-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Child Birthdate</Label>
              <Input type="date" value={form.children[0]?.birthdate || ""} onChange={(e) => updateChild(0, { birthdate: e.target.value })} data-testid="tour-child-dob-input" />
              {isEdit && form.children[0]?.suggestedRoomId && (
                <p className="text-xs text-slate-400 mt-1">Suggested by age: {rooms.find((r) => r.id === form.children[0].suggestedRoomId)?.name || "—"}</p>
              )}
            </div>
            {form.children.slice(1).map((child, i) => {
              const idx = i + 1;
              return (
                <div key={idx} className="col-span-2 grid grid-cols-2 gap-3 relative bg-slate-50 rounded-lg p-2.5 border border-slate-200">
                  <div>
                    <Label className="text-xs text-slate-500">Child {idx + 1} First Name</Label>
                    <Input value={child.firstName} onChange={(e) => updateChild(idx, { firstName: e.target.value })} data-testid={`tour-child-name-input-${idx}`} />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Child {idx + 1} Birthdate</Label>
                    <Input type="date" value={child.birthdate} onChange={(e) => updateChild(idx, { birthdate: e.target.value })} data-testid={`tour-child-dob-input-${idx}`} />
                    {isEdit && child.suggestedRoomId && (
                      <p className="text-xs text-slate-400 mt-1">Suggested by age: {rooms.find((r) => r.id === child.suggestedRoomId)?.name || "—"}</p>
                    )}
                  </div>
                  <button type="button" onClick={() => removeChild(idx)} className="absolute top-1.5 right-1.5 text-slate-400 hover:text-[#C97B7B]" data-testid={`remove-child-btn-${idx}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
            <div className="col-span-2">
              <Button type="button" variant="outline" size="sm" onClick={addChild} data-testid="add-child-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Another Child
              </Button>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Tour Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} data-testid="tour-date-input" />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Start Time *</Label>
                <Input type="time" value={form.startTime} onChange={(e) => set({ startTime: e.target.value })} data-testid="tour-start-time-input" />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-slate-500">End Time</Label>
                <Input type="time" value={form.endTime} onChange={(e) => set({ endTime: e.target.value })} data-testid="tour-end-time-input" />
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Category</Label>
              <Select value={form.category || "__none"} onValueChange={(v) => set({ category: v === "__none" ? "" : v })}>
                <SelectTrigger className="bg-white" data-testid="tour-category-select"><SelectValue placeholder="No category selected" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">No category selected</SelectItem>
                  {PLANNER_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="tour-notes-input" />
            </div>
          </div>

          {/* Parent Reminder */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">Parent Reminder</p>
                <p className="text-xs text-slate-400">Off by default. Reminds the parent before the tour.</p>
              </div>
              <Switch checked={form.parentReminder.enabled} onCheckedChange={(v) => setPR({ enabled: v })} data-testid="parent-reminder-toggle" />
            </div>
            {form.parentReminder.enabled && (
              <div className="space-y-3 pt-1">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <Checkbox checked={form.parentReminder.channels.email} onCheckedChange={(v) => setPR({ channels: { ...form.parentReminder.channels, email: !!v } })} data-testid="parent-reminder-email-checkbox" />
                    <Mail className="w-3.5 h-3.5" /> Email
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <Checkbox checked={form.parentReminder.channels.sms} onCheckedChange={(v) => setPR({ channels: { ...form.parentReminder.channels, sms: !!v } })} data-testid="parent-reminder-sms-checkbox" />
                    <MessageSquare className="w-3.5 h-3.5" /> SMS
                  </label>
                </div>
                <div className="space-y-1.5">
                  <div>
                    <p className="text-xs font-medium text-slate-600">Schedule a reminder</p>
                    <p className="text-[11px] text-slate-400">Choose when to send, then save.</p>
                  </div>
                  <ReminderScheduleFields schedule={form.parentReminder.schedule} onChange={(s) => setPR({ schedule: s })} testIdPrefix="parent-reminder" />
                </div>
                {isEdit && prOccurrences.map((occ, i) => (
                  <div key={occ.index ?? i} className="flex flex-wrap items-center gap-1.5 pt-1">
                    <span className="text-[11px] text-slate-400">Reminder {i + 1} ({occ.offsetMinutesBeforeStart}m before):</span>
                    <DeliveryStatusChip label="Email" channel="email" block={occ.recipients?.parent?.email} testId={`parent-reminder-email-status-${i}`} />
                    <DeliveryStatusChip label="SMS" channel="sms" block={occ.recipients?.parent?.sms} testId={`parent-reminder-sms-status-${i}`} />
                  </div>
                ))}
                {isEdit && (
                  <div className="pt-2 border-t border-slate-200 space-y-1.5">
                    <p className="text-xs font-medium text-slate-600">Send Immediately</p>
                    <Button size="sm" variant="outline" onClick={openParentSendNowConfirm} disabled={sendingNow} data-testid="parent-send-now-btn">
                      <Send className="w-3.5 h-3.5 mr-1.5" /> {sendingNow ? "Sending…" : "Send Now"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Director/Staff Alert — shared recipient + channel selection, used by BOTH the
              scheduled reminder below and the "Send Now" button next to it. */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">Director/Staff Alert</p>
                <p className="text-xs text-slate-400">Off by default. Notifies the Organization Owner and/or staff.</p>
              </div>
              <Switch checked={form.staffAlert.enabled} onCheckedChange={(v) => setSA({ enabled: v })} data-testid="staff-alert-toggle" />
            </div>
            {form.staffAlert.enabled && (
              <div className="space-y-3 pt-1">
                <Button type="button" variant="outline" size="sm" onClick={applyAllStaffOwnerPreset}
                  className="h-7 rounded-full px-3 text-xs border-[#4C8C8C] text-[#4C8C8C] hover:bg-[#4C8C8C]/10" data-testid="staff-alert-preset-all-staff-owner-btn">
                  Notify all staff + owner
                </Button>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <Checkbox checked={form.staffAlert.notifyDirector} onCheckedChange={(v) => setSA({ notifyDirector: !!v })} data-testid="staff-alert-notify-director-checkbox" />
                  Notify Organization Owner ({ownerName})
                </label>
                <div>
                  <Label className="text-xs text-slate-500">Also notify staff (select any)</Label>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-1">
                    {selectableStaffList.length === 0 && <span className="text-xs text-slate-400">No staff records found</span>}
                    {selectableStaffList.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-sm text-slate-600">
                        <Checkbox
                          checked={form.staffAlert.recipientStaffIds.includes(s.id)}
                          onCheckedChange={() => toggleStaffRecipient(s.id)}
                          data-testid={`staff-alert-staff-checkbox-${s.id}`}
                        />
                        {s.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <Checkbox checked={form.staffAlert.channels.email} onCheckedChange={(v) => setSA({ channels: { ...form.staffAlert.channels, email: !!v } })} data-testid="staff-alert-email-checkbox" />
                    <Mail className="w-3.5 h-3.5" /> Email
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-600">
                    <Checkbox checked={form.staffAlert.channels.sms} onCheckedChange={(v) => setSA({ channels: { ...form.staffAlert.channels, sms: !!v } })} data-testid="staff-alert-sms-checkbox" />
                    <MessageSquare className="w-3.5 h-3.5" /> SMS
                  </label>
                </div>

                <div className="space-y-1.5">
                  <div>
                    <p className="text-xs font-medium text-slate-600">Schedule a reminder</p>
                    <p className="text-[11px] text-slate-400">Choose when to send, then save.</p>
                  </div>
                  <ReminderScheduleFields schedule={form.staffAlert.schedule} onChange={(s) => setSA({ schedule: s })} testIdPrefix="staff-alert" />
                </div>

                {isEdit && saOccurrences.map((occ, i) => (
                  <div key={occ.index ?? i} className="flex flex-wrap items-center gap-1.5 pt-1">
                    <span className="text-[11px] text-slate-400">Reminder {i + 1} ({occ.offsetMinutesBeforeStart}m before):</span>
                    {form.staffAlert.notifyDirector && (
                      <>
                        <DeliveryStatusChip label="Owner email" channel="email" block={occ.recipients?.director?.email} testId={`staff-alert-director-email-status-${i}`} />
                        <DeliveryStatusChip label="Owner SMS" channel="sms" block={occ.recipients?.director?.sms} testId={`staff-alert-director-sms-status-${i}`} />
                      </>
                    )}
                    {form.staffAlert.recipientStaffIds.map((id) => {
                      const name = staffList.find((s) => s.id === id)?.name || "Staff";
                      return (
                        <span key={id} className="flex items-center gap-1.5">
                          <DeliveryStatusChip label={`${name} email`} channel="email" block={staffBlock(occ, id, "email")} testId={`staff-alert-staff-email-status-${i}-${id}`} />
                          <DeliveryStatusChip label={`${name} SMS`} channel="sms" block={staffBlock(occ, id, "sms")} testId={`staff-alert-staff-sms-status-${i}-${id}`} />
                        </span>
                      );
                    })}
                  </div>
                ))}
                {isEdit && (
                  <div className="pt-2 border-t border-slate-200 space-y-1.5">
                    <p className="text-xs font-medium text-slate-600">Send Immediately</p>
                    <Button size="sm" variant="outline" onClick={openSendNowConfirm} disabled={sendingNow} data-testid="send-now-btn">
                      <Send className="w-3.5 h-3.5 mr-1.5" /> {sendingNow ? "Sending…" : "Send Now"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          {isEdit ? (
            <Button variant="ghost" className="text-[#C97B7B] hover:bg-[#C97B7B]/10" onClick={cancelTour} data-testid="cancel-tour-btn">
              <Ban className="w-3.5 h-3.5 mr-1.5" /> Cancel Tour
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="schedule-tour-close-btn">Close</Button>
            <Button className="bg-[#4C8C8C] hover:bg-[#3f7575] text-white" onClick={save} disabled={saving} data-testid="schedule-tour-save-btn">
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Schedule Tour"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Send Now confirmation */}
      <Dialog open={!!confirmInfo} onOpenChange={(o) => !o && setConfirmInfo(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="send-now-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-800">Send Reminder Now?</DialogTitle>
          </DialogHeader>
          {confirmInfo && (
            <div className="space-y-2 text-sm text-slate-600">
              <p>This will immediately message:</p>
              <ul className="list-disc list-inside font-medium text-slate-700" data-testid="send-now-confirm-recipients">
                {confirmInfo.names.map((n, idx) => <li key={`${n}-${idx}`}>{n}</li>)}
              </ul>
              <p>Via: <span className="font-medium text-slate-700" data-testid="send-now-confirm-channels">{confirmInfo.channels.join(" & ")}</span></p>
              <p className="text-xs text-slate-400">This does not change the scheduled reminder above.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmInfo(null)} data-testid="send-now-confirm-cancel-btn">Cancel</Button>
            <Button className="bg-[#4C8C8C] hover:bg-[#3f7575] text-white" onClick={confirmSendNow} data-testid="send-now-confirm-send-btn">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

